<template>
  <div>
    <b-modal
      id="import-image-modal"
      :title="modalTitle"
      size="lg"
      hide-footer
      no-close-on-backdrop
      @show="onModalOpen"
      @hide="onModalHide"
    >
      <div
        v-show="currentStep === 1"
        class="upload-image-container mt-3"
      >
        <Vue2Dropzone
          id="ImportImageDropzone"
          ref="fileInput"
          :options="dropzoneOptions"
          @vdropzone-file-added="setFile"
          @vdropzone-removed-file="setFile"
        />
      </div>
      <div
        v-if="templateSelectAvailable"
        class="mb-3"
      >
        <Multiselect
          v-model="selectedLayout"
          :options="layouts"
          :loading="isLoading"
          searchable
          multiple
          :custom-label="customLabel"
          track-by="id"
          class="mb-3"
          placeholder="Select layout"
          @search-change="searchCustomerLayouts"
          @select="(option) => selectedLayout = [option]"
        >
          <template
            v-if="layouts.length < layoutsTotalCount"
            #afterList
          >
            <div class="load-more-btn">
              <b-button
                :disabled="isLoading"
                variant="outline-primary"
                @click="getLayouts"
              >
                Load More
              </b-button>
            </div>
          </template>
        </Multiselect>
        <b-form-radio-group
          v-if="!createLayers"
          v-model="selectedStrategy"
          name="select-strategy"
          :options="options"
        />
        <SelectTemplateBanner
          v-if="!createLayers && selectedStrategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY"
          v-model="selectedTemplates"
          class="mt-3"
          :customers="customers"
        />
      </div>
      <b-form-checkbox
        v-model="createLayers"
        switch
        class="mt-3"
      >
        Create Layers
      </b-form-checkbox>
      <small
        v-if="createLayers"
        class="text-muted d-block"
      >
        Select one image to create layers.
      </small>
      <div 
        class="d-flex "
        :class="currentStep > 1 ? 'justify-content-between' : 'justify-content-end'"
      >
        <b-button
          v-if="currentStep > 1"
          variant="outline-primary"
          class="mt-3"
          @click="handlePrevStep"
        >
          &larr; Previous Step
        </b-button>
        <b-button
          :disabled="!nextStepAvailable"
          variant="outline-primary"
          class="mt-3"
          @click="handleNextStep"
        >
          {{ currentStep < 2 ? 'Next Step ' : 'Import ' }}&rarr;
        </b-button>
      </div>
      <b-overlay
        :show="isImporting"
        no-wrap
      >
        <template #overlay>
          <div class="text-center d-flex flex-column align-items-center justify-content-center">
            <b-spinner
              variant="primary"
              class="mb-2"
            />
            <p class="mb-0">
              {{ loadingText }}
            </p>
          </div>
        </template>
      </b-overlay>
    </b-modal>
    <!--
      Mounted on first use: SelectFolderModal pings auth-only availability
      endpoints (Salsify / Box) as soon as it mounts, and this component is
      rendered on every layout-show page load.
    -->
    <SelectFolderModal
      v-if="isSelectImageModalMounted"
      :modal-id="SELECT_IMAGE_MODAL_ID"
      modal-title="Select Image"
      view-tabs
      :show-layouts="false"
      show-create-layers
      @onSelectedItems="onSelectImageConfirmed"
    />
  </div>
</template>
<script>
import Vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Multiselect from 'vue-multiselect';
import layoutAPI from "@frontend/services/api/layout";
import Customer from "@frontend/services/api/customer";
import SelectTemplateBanner from "@frontend/components/common/atoms/SelectTemplateBanner.vue";
import {
  IMPORT_AND_CREATE_STRATEGY,
  IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY
} from "@frontend/group/modules/import-template-from-psd/import-from-psd-new-template";
import { mapGetters } from "vuex";
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";
import { isExternalAsset } from "@frontend/utils";
import { startSegmentationPreview } from "@frontend/group/modules/image-segmentation/start-preview";

let timer;
const IMPORT_TYPE = {
  BROWSER: 'from_browser',
  DROPZONE: 'from_dropzone'
};
const SELECT_IMAGE_MODAL_ID = 'import-image-select-image-modal';
// Salsify / stock / Box items carry the file extension in `type` instead of the
// normalized 'image' token the file browser uses, so they need an extension check.
const EXTERNAL_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff'];
export default {
  name: 'ImportImageModal',
  components: {
    SelectFolderModal,
    Vue2Dropzone,
    Multiselect,
    SelectTemplateBanner
  },
  data() {
    return {
      importType: IMPORT_TYPE.DROPZONE,
      createLayers: false,
      currentStep: 1,
      isOpen: false,
      file: [],
      dropzoneOptions: {
        url: "http",
        autoProcessQueue: false,
        addRemoveLinks: true,
        createImageThumbnails: true,
        acceptedFiles: "image/*",
        maxFiles: null,
        parallelUploads: 10,
        uploadMultiple: true,
        maxThumbnailFilesize: 50,
        dictDefaultMessage:
          'Paste (CTRL +V), Drag & Drop your files or <a href="#" onclick="event.preventDefault()">Browse</a>',
        dictMaxFilesExceeded: null,
      },
      layoutId: null,
      customers: [],
      selectedTemplates: {
        customer: null,
        templates: [],
      },
      customerId: null,
      layouts: [],
      imagesToImport: [],
      page: 1,
      searchKey: null,
      layoutsTotalCount: null,
      isLoading: false,
      selectedLayout: [],
      isImporting: false,
      options: [
        { text: 'Create new template', value: IMPORT_AND_CREATE_STRATEGY },
        { text: 'Use existing templates', value: IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY },
      ],
      selectedStrategy: IMPORT_AND_CREATE_STRATEGY,
      selectedItems: [],
      isSelectImageModalMounted: false,
      loadingText: 'Importing, please wait...',
      allowedImageTypes: ['image', 'tiff'],
    }
  },
  computed: {
    ...mapGetters('layout', ['selectedInstance']),
    IMPORT_TYPE() {
      return IMPORT_TYPE;
    },
    SELECT_IMAGE_MODAL_ID() {
      return SELECT_IMAGE_MODAL_ID;
    },
    modalTitle() {
      if (this.isImporting) {
        return 'Importing...';
      }

      return this.importType === IMPORT_TYPE.BROWSER ? 'Import Image' : 'Upload Image';
    },
    uploadedFilesCount: function() {
      return this.file.length;
    },
    IMPORT_AND_CREATE_STRATEGY() {
      return IMPORT_AND_CREATE_STRATEGY
    },
    IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY() {
      return IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY;
    },
    importAvailable() {
      switch (this.selectedStrategy) {
        case IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY:
          return this.selectedLayout.length && this.selectedTemplates.templates.length
        case IMPORT_AND_CREATE_STRATEGY:
          return this.selectedLayout.length
        default:
          return false
      }
    },
    nextStepAvailable() {
      if (this.createLayers) {
        const count = this.importType === IMPORT_TYPE.DROPZONE ? this.file.length : this.selectedItems.length;
        return count === 1 && (this.currentStep === 1 || this.selectedLayout.length > 0);
      }
      switch (this.importType) {
        case IMPORT_TYPE.DROPZONE:
          switch (this.currentStep) {
            case 1:
              return this.file.length > 0;
            case 2:
              return this.file.length > 0 && this.importAvailable;
            default:
              return false;

          }
        // The browser flow picks its images in the Select Image popup, so it
        // always enters this modal already on step 2.
        case IMPORT_TYPE.BROWSER:
          switch (this.currentStep) {
            case 2:
              return this.selectedItems.length > 0 && this.importAvailable;
            default:
              return false;
          }
        default:
          return false;
      }
    },
    templateSelectAvailable() {
      return this.currentStep === 2;
    }
  },
  mounted() {
    const importBtn = document.querySelector('.import-from-image-btn');
    if (importBtn) {
      importBtn.addEventListener('click', () => {
        this.$bvModal.show('import-image-modal');
      });
    }

    const browseBtn = document.querySelector('.import-image-browse-btn');
    if (browseBtn) {
      browseBtn.addEventListener('click', this.openSelectImageModal);
    }

    document.addEventListener('paste', this.handlePaste);
  },
  beforeDestroy() {
    document.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    openSelectImageModal() {
      this.isSelectImageModalMounted = true;
      this.$nextTick(() => this.$bvModal.show(SELECT_IMAGE_MODAL_ID));
    },

    // Showing it again straight away would race its own closing animation.
    reopenSelectImageWhenHidden() {
      const onHidden = (bvEvent, modalId) => {
        if (modalId !== SELECT_IMAGE_MODAL_ID) {
          return;
        }

        this.$root.$off('bv::modal::hidden', onHidden);
        this.openSelectImageModal();
      };

      this.$root.$on('bv::modal::hidden', onHidden);
    },

    // Images picked in the Select Image popup skip straight to the layout /
    // strategy step; the popup itself replaces step 1 for this flow.
    onSelectImageConfirmed(items, { createLayers = false } = {}) {
      const images = (items || []).filter((item) => this.isImportableImage(item));

      if (!images.length) {
        toastr.error('None of the selected items is an image');
        // The popup closes itself on OK, so bring it back to let the pick be corrected.
        this.reopenSelectImageWhenHidden();
        return;
      }

      this.selectedItems = images;
      if (createLayers && images.length !== 1) {
        toastr.warning('Select one image to create layers');
        this.reopenSelectImageWhenHidden();
        return;
      }
      this.createLayers = createLayers;
      this.importType = IMPORT_TYPE.BROWSER;
      this.currentStep = 2;
      this.$bvModal.show('import-image-modal');
    },

    isImportableImage(item) {
      if (!item) {
        return false;
      }

      if (this.allowedImageTypes.includes(item.type)) {
        return true;
      }

      if (!isExternalAsset(item)) {
        return false;
      }

      return EXTERNAL_IMAGE_EXTENSIONS.includes(String(item.extension || item.type || '').toLowerCase());
    },

    async onModalOpen() {
      await this.loadCustomers()
      this.page = 1;
      this.searchKey = null;
      this.layouts = [];
      this.selectedLayout = [];
      this.loadCurrentLayoutId();//need to load layout id from url before getting layouts, because this layout id should be present in response, this fox for layout versions
      await this.getLayouts();
      // setting current layout if exists
      if (this.selectedInstance) {
        // getting layout id from url
        const result = window.location.href.match(/group\/(\d+)\/show/);
        if (result && result.length > 1) {
          const found = this.layouts.find((layout) => layout.id === this.layoutId);
          this.selectedLayout = found ? [found] : [];
        }

        const customerId = this.selectedInstance?.data.customer_id;
        if (customerId) {
          this.selectedTemplates.customer = this.customers.find(customer => customer.id === customerId) || null;
        }
      } else {
        this.selectedTemplates.customer = null;
      }
    },
    loadCurrentLayoutId() {
      if (this.selectedInstance) {
        const result = window.location.href.match(/group\/(\d+)\/show/);
        if (result && result.length > 1) {
          this.layoutId = parseInt(result[1]);
        } else {
          this.layoutId = null;
        }
      }
    },
    async setFile() {
      this.$nextTick(() => {
        if (!this.$refs.fileInput) return;
        const files = this.$refs.fileInput.getAcceptedFiles();
        if (files && files.length > 0) {
          this.file = files;
        } else {
          this.file = [];
         
        }
      });
    },
    async loadCustomers() {
      this.customers = (await Customer.getCustomers()).data
    },
    async getLayouts() {
      this.isLoading = true;

      const {
        data:
          {layouts, totalCount}
      } = await layoutAPI.getLayouts({
        params: {
          search: this.searchKey,
          page: this.page,
          withLayoutId: this.layoutId,
          limit: 20
        }
      });

      this.isLoading = false;

      this.layouts = this.layouts.concat(layouts);
      this.layoutsTotalCount = totalCount;
      this.page = this.page += 1;
    },

    customLabel({id, name}) {
      return `${id} - ${name}`;
    },

    searchCustomerLayouts(search) {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        this.page = 1;
        this.searchKey = search;
        this.layouts = [];

        this.getLayouts();
      }, 500);

    },
    processImport() {
      this.handleImportToLayout( {
        layout: this.selectedLayout[0],
        strategy: this.selectedStrategy,
        template_ids: this.selectedTemplates.templates.map(item => item.id),
        customer_id: this.createLayers || this.selectedStrategy === IMPORT_AND_CREATE_STRATEGY
          ? this.selectedLayout[0].customer_id
          : this.selectedTemplates?.customer?.id,
      });
    },
    async handleImportToLayout(payload) {
      this.isImporting = true;
      this.layoutURL = null;
      try {
        let uploadedImage = [];
        if (this.importType === IMPORT_TYPE.DROPZONE) {
          const files = this.$refs.fileInput.getAcceptedFiles();
          if (files.length === 0) {
            toastr.error("No images to import");
            return;
          }
          const formData = new FormData();
          for (let i = 0; i < files.length; i++) {
            formData.append(`file_${i + 1}`, files[i]);
            formData.append(`filename_${i + 1}`, files[i].name);
          }
          formData.append('template_id', payload.template_ids[0]);
          formData.append('customer_id', payload.customer_id);
          const imageResponse = await axios.post('/banner/upload_bk_image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
           uploadedImage = imageResponse.data;
        } else {
          uploadedImage = this.selectedItems.map((item) => {
            let correctPath = item.path;
            if (/share\?file=/.test(item.path)) {
              // Brief references store a relative url, so URL() needs a base or it throws.
              const url = new URL(item.path, window.location.origin);
              correctPath = url.searchParams.get('file');
            }
            return {
            ...item,
            path: correctPath,
          }}).filter((item) => this.isImportableImage(item));
          
        }
        if (this.createLayers) {
          if (uploadedImage.length !== 1) {
            toastr.warning('Select one image to create layers');
            this.isImporting = false;
            return;
          }
          await startSegmentationPreview({
            customerId: payload.customer_id,
            imagePath: uploadedImage[0].path,
            layoutId: payload.layout.id,
            layoutName: null,
            beforeShow: () => {
              this.isImporting = false;
              this.$bvModal.hide('import-image-modal');
            },
          });
          return;
        }
        const createLayoutTemplatePayload = {
          backgroundThemeImages: uploadedImage.map(item => item.path),
          selectedLayout: payload.layout.id,
          strategy: payload.strategy,
        };
        if (payload.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
          createLayoutTemplatePayload.template_ids = payload.template_ids
        }
        const  createLayoutTemplateResponse = await axios.post(`/banner/${payload.customer_id}/group/create_layout_and_template_from_image`, createLayoutTemplatePayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        if (createLayoutTemplateResponse.data && createLayoutTemplateResponse.data.layout_id) {
          this.layoutURL = '/banner/' + createLayoutTemplateResponse.data.customer_id + '/group/' + createLayoutTemplateResponse.data.layout_id + '/show';
          toastr.success("Imported to layout successfully");
          this.loadingText = 'Redirecting to layout, please wait ...';
          window.location.href = this.layoutURL;
        } else {
          toastr.error("Failed to import to layout");
        }
      } catch (error) {
        this.isImporting = false;
        this.currentStep = 2;
        if (error.response && error.response.data && error.response.data.message) {
          toastr.error(error.response.data.message);
        } else {
          toastr.error("Failed to import to layout");
        }
      } 
    },
    handleNextStep() {
      if (this.currentStep <=2) {
        if (this.currentStep === 2 && this.importAvailable) {
          this.processImport();
        } else {
          this.currentStep++;
        }
      }
    },
    
    handlePrevStep() {
      // The browser flow has no step 1 of its own — its picker is the Select
      // Image popup, so going back means reopening it.
      if (this.importType === IMPORT_TYPE.BROWSER) {
        this.$bvModal.hide('import-image-modal');
        this.openSelectImageModal();
        return;
      }

      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    onModalHide() {
      this.isOpen = false;
      this.loadingText = 'Importing, please wait...';
      this.currentStep = 1;
      this.importType = IMPORT_TYPE.DROPZONE;
      this.createLayers = false;
      this.file = [];
      this.selectedItems = [];
      this.selectedLayout = [];
      this.imagesToImport = [];
      this.page = 1;
      this.searchKey = null;
      this.layouts = [];
    },
    handlePaste(event) {
      const modalElement = document.getElementById('import-image-modal');
      if (!modalElement || !modalElement.classList.contains('show')) {
        return;
      }

      // The browser flow shows no dropzone to paste into.
      if (this.importType !== IMPORT_TYPE.DROPZONE) {
        return;
      }

      const items = event.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          const pastedFile = new File(
            [file],
            `pasted-image-${Date.now()}-${i}.png`,
            {
              type: file.type,
            }
          );
          this.$refs.fileInput.addFile(pastedFile);
        }
      }
     
    }
    
  },
}

</script>
